import React, { memo, useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import { Grid, MenuItem } from '@mui/material';
import { isEmpty } from 'ramda';

import {
  DRIVER_FORM_ENTITIES,
  NO_OPTIONS,
  SECTION,
  VEHICLE,
} from '../../../constants/strings';
import {
  validateCollectionStopTime,
  validateDeliveryStopTime,
  validateDriverSectionCode,
  validateDutyEndTime,
  validateDutyStartTime,
  validatePlannedDepartTime,
  validatePlannedReturnTime,
  validateVehicleTypeCode,
} from '../validators';
import { VEHICLE_IS_LGV } from '../messages';

const DriverInputs = ({
  isDisabled,
  vehicles,
  sections,
  control,
  gridClassName,
  onShowMessage,
}) => {
  const vehicleTypeCode = useWatch({
    control,
    name: 'vehicleTypeCode',
    defaultValue: control._formValues?.vehicleTypeCode || '',
  });

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.vehicleTypeCode === vehicleTypeCode),
    [vehicles, vehicleTypeCode]
  );

  useEffect(() => {
    if (selectedVehicle?.lgv && vehicleTypeCode) {
      if (typeof onShowMessage === 'function') {
        onShowMessage(VEHICLE_IS_LGV);
      }
    }
  }, [vehicleTypeCode, selectedVehicle, onShowMessage]);

  return (
    <>
      <Grid container spacing={2} className={gridClassName}>
        <Grid item xs={3}>
          <Controller
            name="dutyStartTime"
            control={control}
            rules={{
              validate: (value) =>
                validateDutyStartTime(value, {
                  ...control._formValues,
                  selectedVehicle,
                }),
            }}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                label={DRIVER_FORM_ENTITIES.DUTY_START}
                type="text"
                error={!!errors.dutyStartTime}
                helperText={errors.dutyStartTime?.message || 'HH:MM'}
                disabled={isDisabled}
                variant="outlined"
                data-testid="duty-start-time"
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <Controller
            name="dutyEndTime"
            control={control}
            rules={{
              validate: validateDutyEndTime,
            }}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                label={DRIVER_FORM_ENTITIES.DUTY_END}
                type="text"
                error={!!errors.dutyEndTime}
                helperText={errors.dutyEndTime?.message || 'HH:MM'}
                disabled={isDisabled}
                variant="outlined"
                data-testid="duty-end-time"
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <Controller
            name="plannedDepartTime"
            control={control}
            rules={{
              validate: validatePlannedDepartTime,
            }}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                label={DRIVER_FORM_ENTITIES.DEPART}
                type="text"
                error={!!errors.plannedDepartTime}
                helperText={errors.plannedDepartTime?.message || 'HH:MM'}
                disabled={isDisabled}
                variant="outlined"
                data-testid="planned-depart-time"
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <Controller
            name="plannedReturnTime"
            control={control}
            rules={{
              validate: validatePlannedReturnTime,
            }}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                label={DRIVER_FORM_ENTITIES.RETURN}
                type="text"
                error={!!errors.plannedReturnTime}
                helperText={errors.plannedReturnTime?.message || 'HH:MM'}
                disabled={isDisabled}
                variant="outlined"
                data-testid="planned-return-time"
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} className={gridClassName}>
        <Grid item xs={3}>
          <Controller
            name="deliveryStopTime"
            control={control}
            rules={{
              validate: validateDeliveryStopTime,
            }}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                label={DRIVER_FORM_ENTITIES.DELIVERY_STOP}
                type="text"
                error={!!errors.deliveryStopTime}
                helperText={errors.deliveryStopTime?.message || 'MM:SS'}
                disabled={isDisabled}
                variant="outlined"
                data-testid="delivery-stop-time"
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <Controller
            name="collectionStopTime"
            control={control}
            rules={{
              validate: validateCollectionStopTime,
            }}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                label={DRIVER_FORM_ENTITIES.COLLECTION_STOP}
                type="text"
                error={!!errors.collectionStopTime}
                helperText={errors.collectionStopTime?.message || 'MM:SS'}
                disabled={isDisabled}
                variant="outlined"
                data-testid="collection-stop-time"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name="vehicleTypeCode"
            control={control}
            rules={{
              validate: validateVehicleTypeCode,
            }}
            render={({ field, formState: { errors } }) => (
              <TextField
                {...field}
                select
                label={VEHICLE}
                type="text"
                error={!!errors.vehicleTypeCode}
                helperText={errors.vehicleTypeCode?.message}
                margin="normal"
                disabled={isDisabled}
                fullWidth
                variant="outlined"
                data-testid="vehicle-type-code"
                sx={{
                  margin: 0,
                }}
              >
                {vehicles.map((vehicle) => (
                  <MenuItem
                    value={vehicle.vehicleTypeCode}
                    key={vehicle.vehicleTypeCode}
                  >
                    {vehicle.vehicleTypeDescription || ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
      </Grid>
      {sections && (
        <Grid container spacing={2} className={gridClassName}>
          <Grid item xs={6}>
            <Controller
              name="sectionCode"
              control={control}
              rules={{
                validate: validateDriverSectionCode,
              }}
              render={({ field, formState: { errors } }) => (
                <TextField
                  {...field}
                  select
                  label={SECTION}
                  type="text"
                  error={!!errors.sectionCode}
                  helperText={errors.sectionCode?.message}
                  margin="normal"
                  disabled={isDisabled}
                  fullWidth
                  variant="outlined"
                  data-testid="section-code"
                  sx={{
                    margin: 0,
                  }}
                >
                  {!isEmpty(sections) ? (
                    sections.map((section) => (
                      <MenuItem
                        value={section.sectionCode}
                        key={section.sectionCode}
                      >
                        {section.sectionName || ''}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">{NO_OPTIONS}</MenuItem>
                  )}
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default memo(DriverInputs);
